import React, { useEffect, useRef, useState } from "react";
import NOLogoIcon from "../../assets/svg/no_logo.svg";
import SelectLogoIcon from "../../assets/svg/select_logo.svg";

const ACCEPTED_TYPES = ["image/png", "image/jpeg"];
const MAX_SIZE_MB = 2;

interface ImageSectionProps {
  heading?: string;
  onSelected?: (file: File) => void;
  wrapperClassName?: string;
  formError?: string;
  fileUrl: string | File | null;
  disabled?: boolean;
}

const ImageSection: React.FC<ImageSectionProps> = ({
  heading = "Image Upload",
  fileUrl,
  onSelected,
  wrapperClassName, // Destructure prop
  formError,
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreview(fileUrl as string);
  }, [fileUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only SVG, JPG, and PNG files are allowed.");
      setPreview(null);
      return;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError("File size must be less than 2 MB.");
      setPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    if (onSelected) {
      onSelected(file);
    }
  };

  return (
    <div className={`${wrapperClassName} w-full p-2 flex flex-col gap-2`}>
      {/* Heading Label */}
      <div className="text-md font-semibold text-black">{heading}</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2rem",
        }}
      >
        {/* Left: Image Preview or Placeholder */}
        <div className="w-[255px] h-[141px] bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="max-w-full max-h-full rounded-lg"
            />
          ) : (
            <div className="flex flex-col items-center">
              <img
                src={NOLogoIcon}
                alt="No logo"
                className="w-[25px] h-[25px] mb-2"
              />
              <span className="text-gray-400">No logo uploaded</span>
            </div>
          )}
        </div>

        {/* Right: File Input */}
        <div
          className={`flex-1 rounded-lg border-2 border-dashed border-gray-200 bg-gray-100 flex flex-col items-center justify-center transition-colors h-full py-6 px-4 relative ${disabled ? "opacity-50 pointer-events-none" : ""}`}
          onDragOver={
            disabled
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add(
                    "border-blue-500",
                    "bg-blue-100"
                  );
                  e.currentTarget.classList.remove(
                    "border-gray-200",
                    "bg-gray-100"
                  );
                }
          }
          onDragLeave={
            disabled
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove(
                    "border-blue-500",
                    "bg-blue-100"
                  );
                  e.currentTarget.classList.add(
                    "border-gray-200",
                    "bg-gray-100"
                  );
                }
          }
          onDrop={
            disabled
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove(
                    "border-blue-500",
                    "bg-blue-100"
                  );
                  e.currentTarget.classList.add(
                    "border-gray-200",
                    "bg-gray-100"
                  );
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    if (fileInputRef.current) {
                      fileInputRef.current.files = dt.files;
                    }
                    handleFileChange({
                      target: { files: dt.files },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }
          }
        >
          <img
            src={SelectLogoIcon}
            alt="No logo"
            className="w-[25px] h-[25px] mb-2"
          />
          <div className="flex items-center gap-3 mt-2">
            <label
              htmlFor="image-upload"
              className={`font-medium text-gray-400 mb-0 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
              style={disabled ? { pointerEvents: "none" } : undefined}
            >
              Drag & Drop or
            </label>
            <input
              id="image-upload"
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              ref={fileInputRef}
              onChange={disabled ? undefined : handleFileChange}
              className="hidden"
              disabled={disabled}
            />
            <span
              className={`${disabled ? "text-gray-500cursor-not-allowed" : " text-accent-blue-dark cursor-pointer"} focus:outline-none`}
              onClick={
                disabled ? undefined : () => fileInputRef.current?.click()
              }
              tabIndex={disabled ? -1 : 0}
              onKeyDown={
                disabled
                  ? undefined
                  : (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        fileInputRef.current?.click();
                      }
                    }
              }
              role="button"
              aria-label="Browse file"
              style={
                disabled ? { pointerEvents: "none", opacity: 0.5 } : undefined
              }
            >
              Browse here
            </span>
          </div>
          <small className="text-gray-400 mt-4">
            Recommended - PNG or JPEG, max 2 mb
          </small>
          {(error || formError) && (
            <div className="text-red-500 mt-2">{error || formError}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageSection;
